
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { cloneDeep, isEqual } from "lodash-es";

import LIST_MARKETPLACE_USERS from "@/apollo/marketplace/ListMarketplaceUsers.gql";

import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import MarketplaceFilters from "@/components/marketplace/MarketplaceFilters.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import ActionPanel from "@/components/common/ActionsPanel.vue";
import LIST_MANAGERS from "@/apollo/managers/ListSingleManagers.gql";
import { makeNameQuery } from "@/helpers/queryHelperFunctions";

import paginationConfig from "@/data/paginationConfig";
import marketplaceFiltersTemplate from "@/data/filtersTemplates/marketplaceFiltersTemplate";

import {
  Company,
  User,
  User_Bool_Exp,
  User_Profile_Bool_Exp,
} from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import PaginationConfig from "@/entities/PaginationConfig";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import MarketplaceFiltersQuery from "@/entities/filters/MarketplaceFiltersQuery";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import {
  getEmptyFiltersResultMessage,
  getEmptyListMessage,
} from "@/helpers/lists/emptyListMessages";
import MarketplaceCard from "@/components/marketplace/MarketplaceCard.vue";
import ElementsSizeProvider from "@/components/uiSize/ElementsSizeProvider.vue";
import CardsContainer from "@/components/card/CardsContainer.vue";
import EmptyListImageMarketplace from "@/components/icons/emptyListImages/EmptyListImageMarketplace.vue";
import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";

@Component({
  components: {
    EmptyListImageMarketplace,
    CardsContainer,
    ElementsSizeProvider,
    MarketplaceCard,
    LoadingOverlay,
    DefaultContentLayout,
    CardPagination,
    MarketplaceFilters,
    EmptyListPlaceholder,
    ActionPanel,
  },
  apollo: {
    marketplaceUsers: {
      query: LIST_MARKETPLACE_USERS,
      variables() {
        return this.marketplaceFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.user.length;
        this.activeResources = data.activeTotal.aggregate.count;
        const users = [...data.user];

      // Shuffle the array of users
      this.shuffleArray(users);

      // Return the shuffled array of users
      return users;
      },
    },
    managers: {
      query: LIST_MANAGERS,
      variables() {
        return this.managerArgs;
      },
      update(data) {
        this.hasAllResourceAccess = data.manager[0].resources_access_allowed;
        return data.manager.resources_access_allowed;
      },
    },
  },
})
export default class MarketplaceContractors extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  marketplaceUsers: User[] | null = null;
  routes = RouteNames;
  activeResources: number = 0;
  hasAllResourceAccess: boolean = false;
  paginationConfig: PaginationConfig = { ...paginationConfig };
  marketplaceFilters: MarketplaceFiltersQuery = cloneDeep(
    marketplaceFiltersTemplate
  );
  get managerArgs() {
    const filterConditions = [
      {
        user: {
          id: { _eq: this.currentUser.id },
        },
      },
    ];
    return {
      where: {
        _and: filterConditions,
      },
    };
  }

  get baseCondition(): User_Bool_Exp {
    const excludeResourceUserId = this.$route.query.exclude_resource;
    const marketplace = {
      marketplace: {
        _eq: true,
      },
    };

    const exclude = !excludeResourceUserId
      ? {}
      : {
          id: {
            _neq: excludeResourceUserId,
          },
        };

    const contractorRole = {
      role: {
        _eq: "CONTRACTOR",
      },
    };

    if (this.$route.query.suitable_resources) {
      return {
        _and: [
          contractorRole,
          exclude,
          {
            _or: [
              marketplace,
              {
                contracts: {
                  company_id: {
                    _eq: this.company.id,
                  },
                },
              },
            ],
          },
        ],
      };
    }

    return {
      _and: [contractorRole, marketplace],
    };
  }

  get hasData(): boolean {
    return Boolean(this.marketplaceUsers);
  }

  get emptyListPlaceholder(): string {
    const entitiesName = "Resources";
    const filtersEmpty = isEqual(
      this.marketplaceFilters,
      marketplaceFiltersTemplate
    );

    if (filtersEmpty) {
      return getEmptyListMessage(entitiesName);
    }

    return 'You currently do not have any resources';
  }

  get loading(): boolean {
    return this.$apollo.queries.marketplaceUsers.loading || this.$apollo.queries.managers.loading;
  }

  get getActiveNumber(): number {
    return this.activeResources;
  }

  get marketplaceTotal(): number | null {
    if (this.paginationConfig.total == 0) {
      return null;
    }
    return this.paginationConfig.total;
  }

  get emptyList(): boolean {
    return !this.loading && !this.marketplaceTotal;
  }

  get marketplaceFilter(): any {
    const { page, limit } = this.paginationConfig;

    const {
      name,
      countries,
      states,
      cities,
      available,
      titles,
      skills,
      minRate,
      maxRate,
    } = this.marketplaceFilters;

    const nextOffset = page * limit - limit;

    const filterConditions: User_Bool_Exp[] = [this.baseCondition];

    if (name) {
      filterConditions.push(makeNameQuery(name));
    }

    if (countries?.length) {
      filterConditions.push({
        profile: {
          country: { _in: countries },
        },
      });
    }

    if (states?.length) {
      filterConditions.push({
        profile: {
          state: getStateQueryConditions({ countries, states }),
        },
      });
    }

    if (cities?.length) {
      filterConditions.push({
        profile: {
          city: { _in: cities },
        },
      });
    }

    if (available !== "all") {
      filterConditions.push({
        available: { _eq: available === "yes" },
      });
    }

    if (titles.length) {
      filterConditions.push({
        profile: {
          job_title: { _in: titles },
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        profile: {
          skills: {
            skill_id: {
              _in: skills,
            },
          },
        },
      });
    }

    if (minRate || maxRate) {
      const minConditions: User_Profile_Bool_Exp[] = [];
      const maxConditions: User_Profile_Bool_Exp[] = [];

      if (minRate) {
        minConditions.push({ min_rate: { _gte: minRate } });
        maxConditions.push({ max_rate: { _gte: minRate } });
      }
      if (maxRate) {
        minConditions.push({ min_rate: { _lte: maxRate } });
        maxConditions.push({ max_rate: { _lte: maxRate } });
      }

      if (minConditions.length && maxConditions.length) {
        filterConditions.push({
          profile: {
            _or: [{ _and: minConditions }, { _and: maxConditions }],
          },
        });
      }
    }

    return {
      offset: nextOffset,
      limit: limit,
      companyId: this.company.id,
      where: {
        _and: filterConditions,
      },
      id: this.currentUser.id,
      managerUserId: this.currentUser.manager?.user_id,
    };
  }

shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
    }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.marketplaceFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: MarketplaceFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;
    const { min_rate: minRate, max_rate: maxRate, ...queryParams } = query;

    const queryData = {
      minRate,
      maxRate,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<MarketplaceFiltersQuery>(
      marketplaceFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.marketplaceUsers?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.loading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const {
      minRate,
      maxRate,
      suitableResources,
      excludeResource,
      ...filtersData
    } = this.marketplaceFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...filtersData,
      min_rate: minRate,
      max_rate: maxRate,
      suitable_resources: suitableResources,
      exclude_resource: excludeResource,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.marketplaceFilters = filters;
    this.paginationConfig = pagination;
  }

  updateFilters(filters: MarketplaceFiltersQuery): void {
    const dataEqual = isEqual(filters, this.marketplaceFilters);

    if (dataEqual) {
      return;
    }

    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
    this.marketplaceFilters = filters;
  }

  isManagerAccessApproved(user: User): boolean {
    return Boolean(user.contracts[0]?.managers[0]?.approved);
  }

  resetFilters(): void {
    if (isEqual(this.marketplaceFilters, marketplaceFiltersTemplate)) {
      return;
    }

    this.marketplaceFilters = cloneDeep(marketplaceFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  refetchCollection(): void {
    window.location.reload();
    this.$apollo.queries.marketplaceUsers.refetch();
  }
}
